import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ListMember, ListMemberRole } from '@cleverlist/types';
import { Button, Input } from '@cleverlist/ui';
import { useAuthStore } from '@cleverlist/store';
import { useApi } from '../providers/ApiProvider';

interface ShareModalProps {
  listId: number;
  isOpen: boolean;
  onClose: () => void;
}

export function ShareModal({ listId, isOpen, onClose }: ShareModalProps) {
  const [inviteRole, setInviteRole] = useState<'editor' | 'viewer'>('editor');
  const [inviteLink, setInviteLink] = useState('');
  const [emailRole, setEmailRole] = useState<'editor' | 'viewer'>('editor');
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [emailError, setEmailError] = useState('');

  const api = useApi();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: members } = useQuery({
    queryKey: ['listMembers', listId],
    queryFn: () => api.getListMembers(listId),
    enabled: isOpen,
  });

  const createLinkMutation = useMutation({
    mutationFn: () => api.shareList(listId, { role: inviteRole }),
    onSuccess: (invite) => {
      const link = `${window.location.origin}/join/${invite.code}`;
      setInviteLink(link);
      setCopied(false);
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: () => api.addListMember(listId, { email, role: emailRole }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listMembers', listId] });
      setEmail('');
      setEmailError('');
    },
    onError: (err) => {
      setEmailError(
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Failed to add member'
      );
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: number) => api.removeListMember(listId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listMembers', listId] });
    },
  });

  const handleCopy = async () => {
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleInviteByEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      addMemberMutation.mutate();
    }
  };

  const roleBadge = (role: ListMemberRole) => {
    const colors: Record<ListMemberRole, string> = {
      owner: 'bg-purple-100 text-purple-700',
      editor: 'bg-blue-100 text-blue-700',
      viewer: 'bg-gray-100 text-gray-700',
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[role]}`}>
        {role}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Share List</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Invite Link Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Invite link</h3>
            <div className="flex gap-2">
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as 'editor' | 'viewer')}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
              <Button
                onClick={() => createLinkMutation.mutate()}
                loading={createLinkMutation.isPending}
                size="sm"
              >
                Create Link
              </Button>
            </div>
            {inviteLink && (
              <div className="mt-2 flex gap-2">
                <input
                  readOnly
                  value={inviteLink}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50"
                />
                <Button onClick={handleCopy} variant="secondary" size="sm">
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            )}
          </div>

          {/* Invite by Email Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Invite by email</h3>
            <form onSubmit={handleInviteByEmail} className="flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="flex-1"
                error={emailError}
              />
              <select
                value={emailRole}
                onChange={(e) => setEmailRole(e.target.value as 'editor' | 'viewer')}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
              <Button
                type="submit"
                loading={addMemberMutation.isPending}
                size="sm"
              >
                Invite
              </Button>
            </form>
          </div>

          {/* Members List */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Members ({members?.length ?? 0})
            </h3>
            <div className="divide-y rounded-lg border">
              {members?.map((member: ListMember) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {member.user?.name}
                        {member.user_id === user?.id && (
                          <span className="text-gray-400 ml-1">(you)</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {member.user?.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {roleBadge(member.role)}
                    {member.role !== 'owner' && member.user_id !== user?.id && (
                      <button
                        onClick={() => removeMemberMutation.mutate(member.user_id)}
                        className="text-gray-400 hover:text-red-500 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
